/* Bravado Labs molecule field.
   One fixed canvas behind the page. Particles settle into hexagon-lattice
   clusters; sections tell it which shape and colors to become.
   API: const m = new Molecule(canvas); m.setTheme({ink, accent}); m.setShape(name);
   Box mode (new Molecule(canvas, { box: true, shape })) sizes the field to the canvas's parent
   instead of the viewport, drifts gently against the cursor, and is torn down with m.destroy(). */
(function () {
  const SHAPES = {
    // cx, cy are fractions of the viewport; r = hex radius in px at 1440w; n = cells
    hero:     { cx: 0.2,  cy: 0.66, r: 44, n: 14, spread: 1.0 },
    right:    { cx: 0.74, cy: 0.5,  r: 40, n: 16, spread: 0.9 },
    left:     { cx: 0.26, cy: 0.5,  r: 40, n: 16, spread: 0.9 },
    chain:    { cx: 0.5,  cy: 0.5,  r: 38, n: 26, spread: 0.6, chain: true },
    scatter:  { cx: 0.5,  cy: 0.5,  r: 52, n: 30, spread: 2.2 },
    quiet:    { cx: 0.86, cy: 0.18, r: 30, n: 8,  spread: 0.7 },
    stage:    { cx: 0.5,  cy: 0.54, r: 46, n: 16, spread: 1.0 },
  };
  const DIRS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];
  const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const rgba = (c, a) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

  class Molecule {
    constructor(canvas, opts = {}) {
      this.cv = canvas;
      this.box = !!opts.box;
      this.par = { x: 0, y: 0 };
      this.ctx = canvas.getContext("2d");
      this.reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.small = innerWidth < 760;
      this.N = this.small ? 84 : 150;
      this.p = Array.from({ length: this.N }, () => ({ x: Math.random(), y: Math.random(), vx: 0, vy: 0, tx: 0.5, ty: 0.5, on: 1 }));
      this.ink = [1, 79, 88]; this.ink2 = this.ink.slice();
      this.acc = [250, 167, 27]; this.acc2 = this.acc.slice();
      this.mouse = { x: -1, y: -1, on: false };
      this.seed = 1;
      this.shape = opts.shape || "hero";
      this.resize();
      this.onMove = (e) => {
        const b = this.box ? this.cv.getBoundingClientRect() : { left: 0, top: 0 };
        this.mouse.x = (e.clientX - b.left) / this.W; this.mouse.y = (e.clientY - b.top) / this.H;
        this.mouse.on = !this.box || (this.mouse.x >= 0 && this.mouse.x <= 1 && this.mouse.y >= 0 && this.mouse.y <= 1);
      };
      this.onLeave = () => (this.mouse.on = false);
      this.onVis = () => { this.paused = document.hidden; };
      addEventListener("pointermove", this.onMove);
      addEventListener("pointerleave", this.onLeave);
      document.addEventListener("visibilitychange", this.onVis);
      if (this.box) { this.ro = new ResizeObserver(() => this.resize()); this.ro.observe(this.cv.parentElement); }
      else { this.onResize = () => this.resize(); addEventListener("resize", this.onResize); }
      this.last = performance.now();
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }
    destroy() {
      this.dead = true; cancelAnimationFrame(this.raf);
      removeEventListener("pointermove", this.onMove); removeEventListener("pointerleave", this.onLeave);
      document.removeEventListener("visibilitychange", this.onVis);
      if (this.ro) this.ro.disconnect(); else removeEventListener("resize", this.onResize);
    }
    resize() {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      const host = this.box ? this.cv.parentElement : null;
      this.W = host ? host.clientWidth : innerWidth; this.H = host ? host.clientHeight : innerHeight;
      if (!this.W || !this.H) return;
      this.cv.width = this.W * dpr; this.cv.height = this.H * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.layout();
    }
    setTheme({ ink, accent }, now) { this.ink2 = hex(ink); this.acc2 = hex(accent); if (now) { this.ink = this.ink2.slice(); this.acc = this.acc2.slice(); } }
    setCenter(c) { this.center = c; this.layout(); }
    setShape(name) {
      if (!SHAPES[name] || name === this.shape) return;
      this.shape = name; this.seed++;
      this.layout();
      if (!this.reduce) this.p.forEach((q) => { q.vx += (Math.random() - 0.5) * 6; q.vy += (Math.random() - 0.5) * 6; });
    }
    layout() {
      const s = this.shape === "hero" && this.center ? { ...SHAPES.hero, ...this.center } : SHAPES[this.shape];
      const k = Math.min(1.15, Math.max(0.62, this.W / (this.box ? 660 : 1440)));
      const R = s.r * k * (this.small ? 0.9 : 1);
      let seed = this.seed * 7919 + 17;
      const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
      const cells = [[0, 0]], seen = new Set(["0,0"]), frontier = [[0, 0]];
      const n = this.box && this.W < 520 ? Math.round(s.n * 1.6) : s.n; // narrow photo boxes: a wider cluster so it shows around the product
      while (cells.length < n && frontier.length) {
        const [a, b] = frontier[Math.floor(rnd() * frontier.length)];
        const dirs = s.chain ? [[0, 1], [0, -1], [1, 0], [-1, 1]] : DIRS;
        const [da, db] = dirs[Math.floor(rnd() * dirs.length)];
        const key = `${a + da},${b + db}`;
        if (!seen.has(key)) { seen.add(key); cells.push([a + da, b + db]); frontier.push([a + da, b + db]); }
      }
      // shared hex vertices: dedupe so bonds join neighbouring rings
      const verts = new Map();
      cells.forEach(([a, b]) => {
        const cx = (a + b / 2) * R * Math.sqrt(3) * s.spread * (s.chain ? 0.55 : 1);
        const cy = b * R * 1.5 * (s.chain ? 1.6 : 1);
        for (let j = 0; j < 6; j++) {
          const ang = (Math.PI / 3) * j + Math.PI / 6;
          const x = cx + Math.cos(ang) * R, y = cy + Math.sin(ang) * R;
          const key = `${Math.round(x / 4)},${Math.round(y / 4)}`;
          if (!verts.has(key)) verts.set(key, [x, y]);
        }
      });
      const pts = [...verts.values()];
      let mx = 0, my = 0; pts.forEach(([x, y]) => { mx += x; my += y; }); mx /= pts.length; my /= pts.length;
      const cxp = s.cx * this.W, cyp = s.cy * this.H;
      this.R = R;
      this.p.forEach((q, i) => {
        if (i < pts.length) { q.tx = (cxp + pts[i][0] - mx) / this.W; q.ty = (cyp + pts[i][1] - my) / this.H; q.on = 1; }
        else { q.tx = rnd(); q.ty = rnd(); q.on = 0; } // spare particles drift as dust
      });
    }
    frame(now) {
      if (this.dead) return;
      this.raf = requestAnimationFrame((t) => this.frame(t));
      if (this.paused || !this.W) return;
      const dt = Math.min(0.05, (now - this.last) / 1000); this.last = now;
      const ease = 1 - Math.pow(0.02, dt);
      this.ink = this.ink.map((v, i) => v + (this.ink2[i] - v) * ease);
      this.acc = this.acc.map((v, i) => v + (this.acc2[i] - v) * ease);
      const { ctx, W, H } = this, t = now / 1000;
      ctx.clearRect(0, 0, W, H);
      const rep = this.mouse.on ? (this.box ? 0.16 : 0.11) : 0;
      // box mode: the whole lattice slides gently away from the cursor, a layer of depth behind the product
      const px = this.box && this.mouse.on && !this.reduce ? (0.5 - this.mouse.x) * 0.07 : 0, py = this.box && this.mouse.on && !this.reduce ? (0.5 - this.mouse.y) * 0.05 : 0;
      this.par.x += (px - this.par.x) * ease * 0.6; this.par.y += (py - this.par.y) * ease * 0.6;
      for (const q of this.p) {
        const ox = q.on ? this.par.x : this.par.x * 0.4, oy = q.on ? this.par.y : this.par.y * 0.4;
        let ax = (q.tx + ox - q.x) * 26, ay = (q.ty + oy - q.y) * 26;
        ax += Math.sin(t * 0.7 + q.tx * 13) * 0.25; ay += Math.cos(t * 0.6 + q.ty * 11) * 0.25;
        if (rep) {
          const dx = q.x - this.mouse.x, dy = (q.y - this.mouse.y) * (H / W), d = Math.hypot(dx, dy);
          if (d < rep && d > 1e-4) { const f = ((rep - d) / rep) * 60; ax += (dx / d) * f; ay += (dy / d) * f; }
        }
        q.vx = (q.vx + ax * dt) * 0.9; q.vy = (q.vy + ay * dt) * 0.9;
        if (this.reduce) { q.x = q.tx; q.y = q.ty; } else { q.x += q.vx * dt; q.y += q.vy * dt; }
      }
      // bonds between active particles that are one hex edge apart
      const lim = (this.R * 1.15) ** 2;
      ctx.lineWidth = 1.4; ctx.strokeStyle = rgba(this.ink, 0.28);
      ctx.beginPath();
      for (let i = 0; i < this.N; i++) {
        const a = this.p[i]; if (!a.on) continue;
        for (let j = i + 1; j < this.N; j++) {
          const b = this.p[j]; if (!b.on) continue;
          const dx = (a.x - b.x) * W, dy = (a.y - b.y) * H;
          if (dx * dx + dy * dy < lim) { ctx.moveTo(a.x * W, a.y * H); ctx.lineTo(b.x * W, b.y * H); }
        }
      }
      ctx.stroke();
      this.p.forEach((q, i) => {
        const accent = q.on && i % 9 === 0;
        ctx.fillStyle = accent ? rgba(this.acc, 0.95) : rgba(this.ink, q.on ? 0.55 : 0.14);
        ctx.beginPath(); ctx.arc(q.x * W, q.y * H, accent ? 4.2 : q.on ? 2.6 : 1.6, 0, 6.283); ctx.fill();
      });
    }
  }
  window.Molecule = Molecule;
})();
